import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Toaster, toast } from 'react-hot-toast';
import {
  AlertCircle,
  Check,
  Server,
  Lock,
  Shield,
  Info,
  Save,
  Building2,
  Zap,
  Edit3,
  Code
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiRequest } from '../utils/api';

// ——————————————— Styled Components ———————————————

const PageWrapper = styled.div`
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  min-height: 100vh;
  padding: 0;
  margin: 0;
`;

const Container = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
  background: white;
  min-height: 100vh;
`;

const MainContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
  background: white;
  position: relative;
`;

const FormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: calc(100vh - 200px);

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const FormSection = styled.div`
  padding: 2rem 2rem; /* reduced padding */
  background: ${props => props.primary ? 'white' : '#fafbfc'};
  border-right: ${props => props.primary ? '1px solid #e5e7eb' : 'none'};
  position: relative;

  @media (max-width: 1200px) {
    border-right: none;
    border-bottom: ${props => props.primary ? '1px solid #e5e7eb' : 'none'};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px; /* reduced height */
    background: ${props => props.primary ? 
      'linear-gradient(90deg, #00419f, #1562b7, #1e90ff)' : 
      'linear-gradient(90deg, #64748b, #475569, #334155)'};
  }
`;

const SectionHeader = styled.div`
  margin-bottom: 1.5rem; /* reduced margin */
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  letter-spacing: -0.01em;

  svg {
    margin-right: 1rem;
    color: ${props => props.primary ? '#00419f' : '#64748b'};
    width: 22px;
    height: 22px;
  }
`;

const SectionDescription = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 2rem;
  grid-template-columns: ${props => props.cols === 2 ? '1fr 1fr' : '1fr'};
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${props => props.mb || '1rem'}; /* reduced margin */
`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem; /* reduced */
  display: flex;
  align-items: center;
`;

const RequiredIndicator = styled.span`
  color: #ef4444;
  margin-left: 0.25rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem; /* reduced padding */
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  font-size: 0.85rem; /* smaller font */
  color: #1f2937;
  transition: all 0.3s ease;
  background: white;
  font-weight: 500;
  height: 2.5rem;

  &:focus {
    outline: none;
    border-color: #00419f;
    box-shadow: 0 0 0 4px rgba(0, 65, 159, 0.1);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #cbd5e1;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }

  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
    opacity: 0.7;
  }

  ${props => props.error && `
    border-color: #ef4444;
    background-color: #fef2f2;

    &:focus {
      border-color: #ef4444;
      box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1);
    }
  `}
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem; /* reduced padding */
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  font-size: 0.85rem; /* smaller font */
  color: #1f2937;
  background: white;
  transition: all 0.3s ease;
  cursor: pointer;
  font-weight: 500;
  height: 2.5rem;

  &:focus {
    outline: none;
    border-color: #00419f;
    box-shadow: 0 0 0 4px rgba(0, 65, 159, 0.1);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #cbd5e1;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.75rem; /* reduced padding */
  border: 2px solid #e5e7eb;
  border-radius: 0.75rem;
  min-height: 80px; /* reduced height */
  resize: vertical;
  font-size: 0.85rem; /* smaller font */
  color: #1f2937;
  transition: all 0.3s ease;
  background: white;
  font-family: inherit;
  font-weight: 500;

  &:focus {
    outline: none;
    border-color: #00419f;
    box-shadow: 0 0 0 4px rgba(0, 65, 159, 0.1);
    transform: translateY(-1px);
  }

  &:hover {
    border-color: #cbd5e1;
  }

  &::placeholder {
    color: #9ca3af;
    font-weight: 400;
  }
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
  margin-left: 0.5rem;
  font-weight: 500;
`;

const InfoCard = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 1rem;
  padding: 1rem 1.5rem; /* reduced padding */
  margin-bottom: 1.5rem; /* reduced margin */
  display: flex;
  align-items: flex-start;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #00419f, #1562b7, #1e90ff);
  }
`;

const InfoIcon = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 0.75rem;
  background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 4px 6px -1px rgba(0, 65, 159, 0.3);
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  color: #00419f;
  margin-bottom: 0.5rem; /* reduced margin */
  letter-spacing: -0.01em;
`;

const InfoText = styled.p`
  color: #1562b7;
  font-size: 0.875rem; /* slightly smaller */
  line-height: 1.4;
  margin: 0;
  font-weight: 500;
`;

const ValidationSummary = styled.div`
  background: ${props => props.valid ?
    'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' :
    'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)'};
  border: 1px solid ${props => props.valid ? '#bbf7d0' : '#fecaca'};
  border-radius: 0.75rem;
  padding: 1rem; /* reduced */
  margin-bottom: 1rem; /* reduced */
  display: flex;
  align-items: center;
`;

const ValidationIcon = styled.div`
  margin-right: 1rem;
  color: ${props => props.valid ? '#22c55e' : '#ef4444'};
`;

const ValidationText = styled.span`
  font-size: 0.75rem; /* smaller */
  font-weight: 600;
  color: ${props => props.valid ? '#166534' : '#dc2626'};
`;

const ActionBar = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-top: 1px solid #e5e7eb;
  padding: 1rem 2rem; /* reduced padding */
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 65, 159, 0.2), transparent);
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.25rem; /* reduced padding */
  font-size: 0.85rem; /* smaller */
  font-weight: 600;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 3rem; /* reduced height */
  position: relative;
  overflow: hidden;
  letter-spacing: -0.01em;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover::before {
    left: 100%;
  }

  ${props =>
    props.primary &&
    `
    background: linear-gradient(180deg, #00419f, #1562b7, #1e90ff);
    color: white;
    border: none;
    box-shadow: 0 3px 6px -1px rgba(0, 65, 159, 0.3);

    &:hover {
      transform: translateY(-1.5px);
      box-shadow: 0 8px 12px -3px rgba(0, 65, 159, 0.4);
    }

    &:active {
      transform: translateY(0);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  `}

  ${props =>
    props.secondary &&
    `
    background: white;
    color: #64748b;
    border: 2px solid #e5e7eb;

    &:hover {
      background: #f8fafc;
      border-color: #cbd5e1;
      transform: translateY(-1px);
      box-shadow: 0 3px 6px -1px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(0);
    }
  `}

  svg {
    margin-right: 0.5rem; /* reduced */
  }
`;

const CurrentValueCard = styled.div`
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 1px solid #f59e0b;
  border-radius: 1rem;
  padding: 1rem 1.25rem; /* reduced padding */
  margin-bottom: 1.5rem; /* reduced margin */
  display: flex;
  align-items: flex-start;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #f59e0b, #d97706, #92400e);
  }
`;

const CurrentValueIcon = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: linear-gradient(180deg, #f59e0b, #d97706);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 0.75rem;
  flex-shrink: 0;
  box-shadow: 0 4px 6px -1px rgba(245, 158, 11, 0.3);
`;

const CurrentValueContent = styled.div`
  flex: 1;
`;

const CurrentValueTitle = styled.h4`
  font-size: 0.8rem; /* smaller */
  font-weight: 600;
  color: #92400e;
  margin-bottom: 0.2rem; /* reduced */
  letter-spacing: -0.01em;
`;

const CurrentValueText = styled.p`
  color: #d97706;
  font-size: 0.75rem; /* smaller */
  line-height: 1.4;
  margin: 0;
  font-weight: 500;
`;

// ——————————————— Component Logic ———————————————

function EditAssetsApi() {
  const navigate = useNavigate();
  const location = useLocation();
  const { asset } = location.state || {};

  const [assetData, setAssetData] = useState({
    oldName: asset?.name || '',
    newName: asset?.name || '',
    apiSpec: asset?.apiSpec || '',
    url: asset?.url || '',
    authType: asset?.authType || '',
    authConfig: asset?.authConfig ? JSON.stringify(asset.authConfig, null, 2) : ''
  });

  const [validation, setValidation] = useState({
    step1Valid: false,
    step2Valid: true // Authentication is optional
  });

  // URL validation function
  const isValidUrl = (url) => {
    if (!url) return false;
    const urlPattern = /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/;
    return urlPattern.test(url.trim());
  };

  // JSON validation function
  const isValidJson = (jsonString) => {
    if (!jsonString.trim()) return true; // Empty is valid (optional)
    try {
      const parsed = JSON.parse(jsonString);
      return typeof parsed === 'object' && !Array.isArray(parsed);
    } catch {
      return false;
    }
  };

  // Validate forms
  useEffect(() => {
    const step1Valid = 
      assetData.newName.trim() !== '' && 
      assetData.apiSpec.trim() !== '' &&
      isValidUrl(assetData.apiSpec) &&
      (assetData.url === '' || isValidUrl(assetData.url));

    const step2Valid = isValidJson(assetData.authConfig);

    setValidation({
      step1Valid,
      step2Valid
    });
  }, [assetData]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setAssetData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveChanges = () => {
    if (!validation.step1Valid) {
      toast.error('Please fill all required fields correctly');
      return;
    }

    if (!validation.step2Valid) {
      toast.error('Auth Config must be valid JSON');
      return;
    }

    // Parse authConfig if provided
    let parsedAuthConfig = {};
    if (assetData.authConfig.trim()) {
      try {
        parsedAuthConfig = JSON.parse(assetData.authConfig);
      } catch (err) {
        toast.error('Auth Config must be valid JSON');
        return;
      }
    }

    const payload = {
      oldName: assetData.oldName,
      newName: assetData.newName.trim(),
      assetType: 'api',
      apiSpec: assetData.apiSpec.trim(),
      url: assetData.url.trim() || null,
      authType: assetData.authType.trim() || null,
      authConfig: parsedAuthConfig,
      backend: null,
      loginSequence: {},
      ips: []
    };

    apiRequest('/api/assets/edit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(async res => {
        const data = await res.json();
        if (res.ok) {
          toast.success(data.message || 'API asset updated successfully!');
          navigate('/add-assets');
        } else {
          const serverError = data.error ?? data.message ?? 'Unknown validation error.';
          throw new Error(serverError);
        }
      })
      .catch(error => {
        console.error('Error updating API asset:', error);
        toast.error(error.message || 'An error occurred while updating the asset');
      });
  };

  return (
    <PageWrapper>
      <Container>

        <MainContent>
          <FormContainer>
            {/* API Configuration Section */}
            <FormSection primary>
              <SectionHeader>
                <SectionTitle primary>
                  <Server size={28} />
                  API Configuration
                </SectionTitle>
                <SectionDescription>
                  Update your API endpoint specifications and OpenAPI documentation URLs. 
                  Modify configuration while maintaining automated security assessment capabilities.
                </SectionDescription>
              </SectionHeader>

              <CurrentValueCard>
                <CurrentValueIcon>
                  <Info size={16} />
                </CurrentValueIcon>
                <CurrentValueContent>
                  <CurrentValueTitle>Current API Asset</CurrentValueTitle>
                  <CurrentValueText>
                    Editing: {assetData.oldName || 'Unnamed API Asset'}
                  </CurrentValueText>
                </CurrentValueContent>
              </CurrentValueCard>

              <InfoCard>
                <InfoIcon>
                  <Code size={24} />
                </InfoIcon>
                <InfoContent>
                  <InfoTitle>Enhanced OpenAPI Integration</InfoTitle>
                  <InfoText>
                    Updated specifications are automatically validated and analyzed for security 
                    vulnerabilities. Our platform maintains backward compatibility while implementing 
                    the latest OpenAPI 3.0+ standards and security best practices.
                  </InfoText>
                </InfoContent>
              </InfoCard>

              <ValidationSummary valid={validation.step1Valid}>
                <ValidationIcon valid={validation.step1Valid}>
                  {validation.step1Valid ? <Check size={20} /> : <AlertCircle size={20} />}
                </ValidationIcon>
                <ValidationText valid={validation.step1Valid}>
                  {validation.step1Valid 
                    ? 'All required API configuration fields are completed' 
                    : 'Please complete all required fields with valid URLs'}
                </ValidationText>
              </ValidationSummary>

              <FormGrid cols={2}>
                <FormGroup>
                  <Label>
                    Current Name (Read-Only)
                  </Label>
                  <Input
                    type="text"
                    value={assetData.oldName}
                    disabled
                    style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>
                    New API Asset Name <RequiredIndicator>*</RequiredIndicator>
                  </Label>
                  <Input
                    type="text"
                    name="newName"
                    value={assetData.newName}
                    onChange={handleInputChange}
                    placeholder="Updated User API, Enhanced Payment Gateway"
                  />
                </FormGroup>
              </FormGrid>

              <FormGroup>
                <Label>
                  OpenAPI Specification URL <RequiredIndicator>*</RequiredIndicator>
                  {assetData.apiSpec && !isValidUrl(assetData.apiSpec) && (
                    <ErrorMessage>
                      Please enter a valid URL format
                    </ErrorMessage>
                  )}
                </Label>
                <Input
                  type="text"
                  name="apiSpec"
                  value={assetData.apiSpec}
                  onChange={handleInputChange}
                  placeholder="https://api.company.com/swagger.json"
                  error={assetData.apiSpec && !isValidUrl(assetData.apiSpec)}
                />
              </FormGroup>

              <FormGroup mb="0">
                <Label>
                  Base URL (Optional)
                  <Info size={16} style={{ marginLeft: '0.5rem', color: '#6b7280' }} />
                  {assetData.url && !isValidUrl(assetData.url) && (
                    <ErrorMessage>
                      Please enter a valid URL format
                    </ErrorMessage>
                  )}
                </Label>
                <Input
                  type="text"
                  name="url"
                  value={assetData.url}
                  onChange={handleInputChange}
                  placeholder="https://api.company.com"
                  error={assetData.url && !isValidUrl(assetData.url)}
                />
              </FormGroup>
            </FormSection>

            {/* Authentication Section */}
            <FormSection>
              <SectionHeader>
                <SectionTitle>
                  <Lock size={28} />
                  Authentication Configuration
                </SectionTitle>
                <SectionDescription>
                  Update authentication methods and security configurations for comprehensive 
                  API testing and enhanced vulnerability assessment of protected endpoints.
                </SectionDescription>
              </SectionHeader>

              <InfoCard>
                <InfoIcon>
                  <Shield size={24} />
                </InfoIcon>
                <InfoContent>
                  <InfoTitle>Advanced Security Management</InfoTitle>
                  <InfoText>
                    Updated authentication credentials are protected with next-generation encryption 
                    and stored in compliance with SOC 2 Type II standards. All modifications are 
                    audited and require multi-factor authentication for administrative access.
                  </InfoText>
                </InfoContent>
              </InfoCard>

              <ValidationSummary valid={validation.step2Valid}>
                <ValidationIcon valid={validation.step2Valid}>
                  {validation.step2Valid ? <Check size={20} /> : <AlertCircle size={20} />}
                </ValidationIcon>
                <ValidationText valid={validation.step2Valid}>
                  {validation.step2Valid 
                    ? 'Authentication configuration is valid' 
                    : 'Auth Config must be valid JSON format'}
                </ValidationText>
              </ValidationSummary>

              <FormGroup>
                <Label>
                  Authentication Type
                  <Info size={16} style={{ marginLeft: '0.5rem', color: '#6b7280' }} />
                </Label>
                <Select
                  name="authType"
                  value={assetData.authType}
                  onChange={handleInputChange}
                >
                  <option value="">Select authentication method</option>
                  <option value="apiKey">API Key</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="basic">Basic Authentication</option>
                  <option value="oauth2">OAuth 2.0</option>
                  <option value="custom">Custom Header</option>
                </Select>
              </FormGroup>

              <FormGroup mb="0">
                <Label>
                  Authentication Configuration (JSON)
                  <Info size={16} style={{ marginLeft: '0.5rem', color: '#6b7280' }} />
                  {assetData.authConfig && !isValidJson(assetData.authConfig) && (
                    <ErrorMessage>
                      Must be valid JSON format
                    </ErrorMessage>
                  )}
                </Label>
                <TextArea
                  name="authConfig"
                  value={assetData.authConfig}
                  onChange={handleInputChange}
                  placeholder={`{
  "headerName": "Authorization",
  "in": "header",
  "value": "Bearer your-token-here"
}`}
                  error={assetData.authConfig && !isValidJson(assetData.authConfig)}
                />
              </FormGroup>
            </FormSection>
          </FormContainer>

          <ActionBar>
            <Button secondary onClick={() => navigate('/add-assets')}>
              Cancel Changes
            </Button>
            <Button 
              primary 
              onClick={handleSaveChanges}
              disabled={!validation.step1Valid || !validation.step2Valid}
            >
              <Save size={20} />
              Update API Asset
            </Button>
          </ActionBar>
        </MainContent>
      </Container>

      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'white',
            color: '#1e293b',
            border: '1px solid #e5e7eb',
            borderRadius: '0.75rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            fontWeight: '500',
          },
        }}
      />
    </PageWrapper>
  );
}

export default EditAssetsApi;
